import { useCallback, useEffect, useRef } from "react";

import { clearUserActionTriggerIntent, getMessageBus, persistUserActionTriggerIntent } from "@jti/fe-message-bus";

import type { OnsitePersonalizationModalTrigger, UserActionTrigger } from "../types";

const POST_AUTH_SETTLE_DELAY_MS = 300;
type ModalOpenTrigger = OnsitePersonalizationModalTrigger;
type PostAuthModalOpenTrigger = Extract<UserActionTrigger, "completeRegistration" | "loginSuccess">;
type PopupBlocker = "welcome" | "province";
type PopupBlockerState = Record<PopupBlocker, boolean>;

const getHasBlockingPopup = (popupBlockers: PopupBlockerState) => Object.values(popupBlockers).some(Boolean);
const getInitialPopupBlockerState = (): PopupBlockerState => ({ welcome: false, province: false });
const isPostAuthTrigger = (trigger: ModalOpenTrigger): trigger is PostAuthModalOpenTrigger =>
  trigger === "completeRegistration" || trigger === "loginSuccess";
const persistQueuedPostAuthTrigger = (trigger: PostAuthModalOpenTrigger) => {
  persistUserActionTriggerIntent(trigger, window.location.href);
};

/**
 * Coordinates OnsitePersonalizationModal with blocking post-auth popups.
 *
 * While a blocking popup is open, any open attempt is suppressed and a single
 * deferred attempt is queued. After all blocking popups close, the queued attempt
 * is replayed. Post-auth triggers (loginSuccess / completeRegistration) always
 * get an extra settle delay so welcome/province popups can publish their state
 * before the onsite modal opens.
 *
 * Design notes:
 * - Does NOT seed initial state from sticky history directly: instead it lets the
 *   subscribe callback carry the sticky replay synchronously on mount. This avoids
 *   a double-count when both the useState initializer and the subscribe replay
 *   would otherwise both "see" the same sticky value.
 * - Subscribes by token (not by topic) so unsubscribing does not kill other
 *   consumers on the same channel.
 */
export function useWelcomeQueue(openModal: (trigger?: ModalOpenTrigger) => void, currentTrigger?: UserActionTrigger) {
  const openModalRef = useRef(openModal);
  // Start closed; the subscribe effect below replays sticky states on mount
  // and sets blockers correctly before the first user interaction can occur.
  const popupBlockersRef = useRef<PopupBlockerState>(getInitialPopupBlockerState());
  const pendingTriggerRef = useRef<ModalOpenTrigger | null>(null);
  const replayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    openModalRef.current = openModal;
  }, [openModal]);

  const clearReplayTimeout = useCallback(() => {
    if (replayTimeoutRef.current) {
      clearTimeout(replayTimeoutRef.current);
      replayTimeoutRef.current = null;
    }
  }, []);

  const replayPendingTrigger = useCallback(
    (pendingTrigger: ModalOpenTrigger) => {
      clearReplayTimeout();

      if (isPostAuthTrigger(pendingTrigger)) {
        replayTimeoutRef.current = setTimeout(() => {
          replayTimeoutRef.current = null;

          if (getHasBlockingPopup(popupBlockersRef.current)) {
            return;
          }

          if (pendingTriggerRef.current !== pendingTrigger) {
            return;
          }
          pendingTriggerRef.current = null;
          clearUserActionTriggerIntent();
          openModalRef.current(pendingTrigger);
        }, POST_AUTH_SETTLE_DELAY_MS);

        return;
      }
      pendingTriggerRef.current = null;
      openModalRef.current(pendingTrigger);
    },
    [clearReplayTimeout]
  );

  // Subscribe by token so we don't nuke other consumers on cleanup.
  // MessageBus.subscribe() synchronously replays the last sticky value, which
  // sets blocker states to the correct initial state within the same effect flush.
  useEffect(() => {
    const messageBus = getMessageBus();
    const handlePopupBlockerChange = (name: PopupBlocker, channel: string, isOpen: boolean) => {
      const currentPopupBlockers = popupBlockersRef.current;

      if (currentPopupBlockers[name] === isOpen) {
        return;
      }
      const nextPopupBlockers = { ...currentPopupBlockers, [name]: isOpen };
      popupBlockersRef.current = nextPopupBlockers;

      if (getHasBlockingPopup(nextPopupBlockers)) {
        clearReplayTimeout();
        return;
      }

      if (pendingTriggerRef.current) {
        replayPendingTrigger(pendingTriggerRef.current);
      }
    };

    const welcomeToken = messageBus.subscribe({
      channel: "welcome.popup.state",
      callback: ({ payload }) => {
        handlePopupBlockerChange("welcome", "welcome.popup.state", payload.isOpen);
      },
    });
    const provinceToken = messageBus.subscribe({
      channel: "province.popup.state",
      callback: ({ payload }) => {
        handlePopupBlockerChange("province", "province.popup.state", payload.isOpen);
      },
    });
    const tokens = [welcomeToken, provinceToken];

    return () => {
      tokens.forEach((token) => {
        messageBus.unsubscribeToken(token);
      });
    };
  }, [clearReplayTimeout, replayPendingTrigger]);

  useEffect(() => clearReplayTimeout, [clearReplayTimeout]);

  const openModalWithWelcomeQueue = useCallback(
    (trigger?: ModalOpenTrigger) => {
      const resolvedTrigger = trigger ?? currentTrigger ?? "default";

      if (getHasBlockingPopup(popupBlockersRef.current)) {
        // Queue only the latest attempt — one replay is enough.
        pendingTriggerRef.current = resolvedTrigger;
        if (isPostAuthTrigger(resolvedTrigger)) {
          persistQueuedPostAuthTrigger(resolvedTrigger);
        }
        clearReplayTimeout();
        return;
      }

      if (isPostAuthTrigger(resolvedTrigger)) {
        pendingTriggerRef.current = resolvedTrigger;
        persistQueuedPostAuthTrigger(resolvedTrigger);
        replayPendingTrigger(resolvedTrigger);
        return;
      }

      openModal(resolvedTrigger);
    },
    [openModal, currentTrigger, clearReplayTimeout, replayPendingTrigger]
  );

  return openModalWithWelcomeQueue;
}
